import type { components, paths } from '@fluencelabs/vodopad-admin-api'
import type { Client } from 'openapi-fetch'

import { unwrapApiData } from '@/api/unwrapApiResult'

type Api = Pick<Client<paths>, 'GET'>
export type IpFamily = components['schemas']['IpAddressType']
export type IpStats = Record<IpFamily, { used: bigint; free: bigint }>

const MAX_PER_PAGE = 200

async function fetchStoragePage(api: Api, page: number) {
  const result = await api.GET('/v1/admin/storages', {
    params: { query: { page, perPage: MAX_PER_PAGE, status: 'ready' } },
  })
  return unwrapApiData(result, 'Failed to load storages')
}

export async function fetchReadyStorageGb(api: Api): Promise<number> {
  const first = await fetchStoragePage(api, 1)
  const rest = await Promise.all(
    Array.from(
      { length: Math.max(first.pagination.totalPages - 1, 0) },
      (_, i) => fetchStoragePage(api, i + 2),
    ),
  )
  return [first, ...rest]
    .flatMap((page) => page.items)
    .reduce((sum, storage) => sum + storage.volumeGb, 0)
}

/** Exact pool counts from the last hardware measurement of every live cluster. */
export async function fetchIpStats(api: Api): Promise<IpStats> {
  const result = await api.GET('/v1/admin/analytics/cpu/capacity')
  const clusters = unwrapApiData(result, 'Failed to load capacity').clusters
  const stats: IpStats = {
    V4: { used: 0n, free: 0n },
    V6: { used: 0n, free: 0n },
  }
  for (const cluster of clusters) {
    if (cluster.removedAt) continue
    const pools = cluster.inventory?.publicIps
    if (!pools) {
      throw new Error(`Cluster ${cluster.name} has no measured IP pools`)
    }
    for (const [family, pool] of [
      ['V4', pools.ipv4],
      ['V6', pools.ipv6],
    ] as const) {
      stats[family].used += BigInt(pool.claimed) + BigInt(pool.otherClaimed)
      stats[family].free += BigInt(pool.free)
    }
  }
  return stats
}
